import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, DistributedTracingModes } from '@microsoft/applicationinsights-web';

import { getEnv } from '@apple/utils/config/env';

export type InitTelemetry = {
	telemetry: ReactPlugin;
};

export let ai: ApplicationInsights | null = null;

export function configAppInsights(options: {
	connectionString: string;
	roleTag: string;
}): InitTelemetry {
	const telemetry = new ReactPlugin();
	const inDev = getEnv().DEV;
	if (!options.roleTag?.trim()) {
		throw new Error('A frontend Application Insights role name is required.');
	}

	ai = new ApplicationInsights({
		config: {
			connectionString: options.connectionString,
			extensions: [telemetry],
			disableTelemetry: inDev,
			enableDebug: inDev,
			enableAutoRouteTracking: true,
			disableAjaxTracking: false,
			enableAjaxPerfTracking: true,
			autoTrackPageVisitTime: true,
			enableCorsCorrelation: true,
			distributedTracingMode: DistributedTracingModes.W3C,
			enableRequestHeaderTracking: true,
			enableResponseHeaderTracking: true,
			enableUnhandledPromiseRejectionTracking: true,
			samplingPercentage: 100,
			loggingLevelConsole: inDev ? 2 : 1,
		},
	});
	ai.loadAppInsights();
	ai.addTelemetryInitializer(item => {
		const env = item;
		env.tags = env.tags || [];
		env.tags['ai.cloud.role'] = options.roleTag;
		env.data = {
			...env.data,
			'deployment.environment.name': getEnv().MODE,
		};
	});

	return { telemetry } as const;
}

export function trackClientException(options: {
	error: unknown;
	operationName: string;
	properties?: Record<string, string | number | boolean>;
	measurements?: Record<string, number>;
}): void {
	try {
		ai?.trackException({
			exception:
				options.error instanceof Error ? options.error : new Error(String(options.error)),
			properties: {
				operation: options.operationName,
				...options.properties,
			},
			measurements: options.measurements,
			severityLevel: 3,
		});
	} catch {
		// Telemetry must never interfere with the application error path.
	}
}

export function telemetryOperationName(key: unknown, fallback: string): string {
	const candidate = Array.isArray(key) ? key[0] : key;
	if (typeof candidate !== 'string' || candidate.trim().length === 0) {
		return fallback;
	}

	return candidate
		.replace(/[?#].*$/, '')
		.replace(
			/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
			':id',
		)
		.replace(/\/(\d+)(?=\/|$)/g, '/:id')
		.slice(0, 128);
}

export function telemetryFailureReason(reason: unknown): string {
	if (reason instanceof Error) {
		return `${reason.name}: ${reason.message}`;
	}

	return reason == null ? '' : String(reason);
}
